import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal, Select, Spin, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { liveFollowAPI, tradeAPI } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const parseJsonSafe = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};

function Field({ label, value }) {
  const v = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <div style={{ width: 80, color: '#666', flex: '0 0 auto' }}>{label}</div>
      <div style={{ color: '#111', flex: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{v}</div>
    </div>
  );
}

const MobileTradeRecords = () => {
  const navigate = useNavigate();
  const [tradeRecords, setTradeRecords] = useState([]);
  const [follows, setFollows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFollow, setSelectedFollow] = useState(null);

  const fetchFollows = useCallback(async () => {
    try {
      const response = await tradeAPI.getLiveFollows();
      const list = response.data || [];
      setFollows(list);
      if (!selectedFollow && list.length > 0 && list[0] && list[0].id) {
        setSelectedFollow(list[0].id);
      }
    } catch (error) {
      message.error('获取实盘列表失败');
    }
  }, [selectedFollow]);

  const fetchTradeRecords = useCallback(async () => {
    try {
      if (!selectedFollow) {
        setTradeRecords([]);
        return;
      }
      const response = await liveFollowAPI.getJobRecords(selectedFollow, 100, 0);
      setTradeRecords(response.data || []);
    } catch (error) {
      message.error('获取交易记录失败');
    } finally {
      setLoading(false);
    }
  }, [selectedFollow]);

  useEffect(() => {
    fetchFollows();
  }, [fetchFollows]);

  useEffect(() => {
    setLoading(true);
    fetchTradeRecords();
  }, [fetchTradeRecords]);

  const handleRefresh = () => {
    fetchTradeRecords();
  };

  const handleBack = () => {
    navigate('/topic-records', { replace: true });
  };

  const formatCreatedAt = (date) => {
    return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const formatRunAt = (date) => {
    return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const getMessage = (record) => {
    const obj = parseJsonSafe(record?.raw_payload);
    return obj?.message || record?.message || '-';
  };

  const getDiffLines = (record) => {
    const diffLines = record?.diff_lines;
    if (!diffLines) return '-';

    try {
      const parsed = typeof diffLines === 'string' ? JSON.parse(diffLines) : diffLines;
      if (!Array.isArray(parsed) || parsed.length === 0) return '-';

      return parsed
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }
          return String(item);
        })
        .join('\n');
    } catch (e) {
      return String(diffLines);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 16px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
            实盘记录
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={handleRefresh}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              刷新
            </Button>
            <Button
              size="small"
              onClick={handleBack}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              返回
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin />
          </div>
        ) : (
          <div>
            <Card style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
              <Select placeholder="选择实盘" style={{ width: '100%' }} value={selectedFollow} onChange={(v) => setSelectedFollow(v)} allowClear>
                {follows.map((f) => (
                  <Option key={f.id} value={f.id}>
                    {`${f.id} - ${f.alias || f.name || '实盘'}`}
                  </Option>
                ))}
              </Select>
            </Card>

            {(tradeRecords || []).map((record, idx) => (
              <Card key={record?.id ?? idx} style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
                <Field label="ID" value={record?.id} />
                <Field label="message" value={getMessage(record)} />
                <Field label="diff_lines" value={getDiffLines(record)} />
                <Field label="状态" value={record?.status} />
                <Field label="时间" value={formatRunAt(record?.run_at)} />
              </Card>
            ))}

            {!tradeRecords || tradeRecords.length === 0 ? (
              <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 12 } }}>
                <Text type="secondary">暂无数据</Text>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileTradeRecords;
